import { useMutation, useQueryClient } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';
import { useStoreState, useStoreActions } from '~/store';
import { api } from '~/api';
import { Dayjs } from 'dayjs';

interface MarketplacePresetData {
  name: string;
  solution: string;
  data: {
    fDivision?: string[];
    fProducts?: string[];
    fTags?: string[];
    fType?: string[];
    fEtd?: Dayjs[];
    fEtdOption?: string;
    fUpdated?: Dayjs[];
    fUpdatedOption?: string;
  };
}

interface MarketplacePreset {
  id: string;
  name: string;
  solution: string;
  data: MarketplacePresetData['data'];
}

interface UserPrefs {
  marketplace?: {
    presets?: MarketplacePreset[];
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface StoreState {
  auth: {
    userPrefs: UserPrefs;
  };
}

interface StoreActions {
  auth: {
    updateUser: (payload: { prefs: UserPrefs }) => void;
  };
}

export const useSaveMarketplacePreset = () => {
  const queryClient = useQueryClient();
  const userPrefs = useStoreState((state: StoreState) => state.auth.userPrefs);
  const updateUser = useStoreActions(
    (actions: StoreActions) => actions.auth.updateUser
  );

  return useMutation({
    mutationFn: async (payload: MarketplacePresetData) => {
      const prefs: UserPrefs = userPrefs || {};
      const presets = [...(prefs?.marketplace?.presets ?? [])];

      // Add new preset with generated UUID
      presets.push({ id: uuid(), ...payload });

      const newPrefs: UserPrefs = {
        ...prefs,
        marketplace: { ...(prefs.marketplace ?? {}), presets },
      };

      // Optimistically update local state
      updateUser({ prefs: newPrefs });

      // Save to server
      const response = await api.patchData('/user/prefs', newPrefs);
      return response;
    },
    onSuccess: (response, variables) => {
      // Update with server response
      updateUser({ prefs: response });

      // Invalidate any queries that depend on user prefs
      queryClient.invalidateQueries({ queryKey: ['userPrefs'] });

      toast.success(`Preset "${variables.name}" is saved`);
    },
    onError: (error: unknown) => {
      // Revert optimistic update by refetching user prefs
      queryClient.invalidateQueries({ queryKey: ['userPrefs'] });

      const errorMsg = (
        error as {
          response?: { data?: { errors?: Array<{ description?: string }> } };
        }
      )?.response?.data?.errors?.[0]?.description;
      if (errorMsg) {
        toast.error(errorMsg);
      } else {
        toast.error(
          'An error occurred while saving preset. Please try again later.'
        );
      }
    },
  });
};

export const useDeleteMarketplacePreset = () => {
  const queryClient = useQueryClient();
  const userPrefs = useStoreState((state: StoreState) => state.auth.userPrefs);
  const updateUser = useStoreActions(
    (actions: StoreActions) => actions.auth.updateUser
  );

  return useMutation({
    mutationFn: async (id: string) => {
      const prefs: UserPrefs = userPrefs || {};
      const presets = (prefs?.marketplace?.presets ?? []).filter(
        item => item.id !== id
      );

      const newPrefs: UserPrefs = {
        ...prefs,
        marketplace: { ...(prefs.marketplace ?? {}), presets },
      };

      // Optimistically update local state
      updateUser({ prefs: newPrefs });

      // Save to server
      const response = await api.patchData('/user/prefs', newPrefs);
      return response;
    },
    onSuccess: response => {
      // Update with server response
      updateUser({ prefs: response });

      // Invalidate any queries that depend on user prefs
      queryClient.invalidateQueries({ queryKey: ['userPrefs'] });

      toast.success('Preset deleted');
    },
    onError: (error: unknown) => {
      // Revert optimistic update by refetching user prefs
      queryClient.invalidateQueries({ queryKey: ['userPrefs'] });

      const errorMsg = (
        error as {
          response?: { data?: { errors?: Array<{ description?: string }> } };
        }
      )?.response?.data?.errors?.[0]?.description;
      if (errorMsg) {
        toast.error(errorMsg);
      } else {
        toast.error(
          'An error occurred while deleting preset. Please try again later.'
        );
      }
    },
  });
};
